import React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { ReusableDialog } from "../../../../../components/popupModals/ReusableDialog";
import Loader from "../../../../../components/LoadSpinner";
import { useCheckinExistingFlow } from "../../../../../redux/hooks/useCheckinExistingFlow";
import { CHECKIN_EXISTING_STATUS } from "../../../../../redux/slices/documents/checkinExistingFlowSlice";
import {
  CHECKIN_EXISTING_DOCUMENT_TITLE,
  CHECKED_OUT_ALREADY,
  CHECKED_OUT_ALREADY_MESSAGE,
} from "../../../../../redux/constants/messageTypes";

const ERROR_DEFAULT_MESSAGE = CHECKED_OUT_ALREADY_MESSAGE;

function resolveCheckinExistingErrorTitle(message) {
  const text = typeof message === "string" ? message : "";
  if (text.includes(CHECKED_OUT_ALREADY_MESSAGE)) {
    return CHECKED_OUT_ALREADY;
  }
  return CHECKIN_EXISTING_DOCUMENT_TITLE;
}

const STEP_TITLES = {
  [CHECKIN_EXISTING_STATUS.DOWNLOADING]: "Fetching from OneDrive",
  [CHECKIN_EXISTING_STATUS.UPLOADING]: "Check-in existing",
  [CHECKIN_EXISTING_STATUS.DELETING]: "Deleting document",
};

const STEP_MESSAGES = {
  [CHECKIN_EXISTING_STATUS.DOWNLOADING]: {
    line1: "Fetching data from OneDrive",
    line2: "Please, wait...",
  },
  [CHECKIN_EXISTING_STATUS.UPLOADING]: {
    line1: "Downloading from OneDrive and uploading to IFS",
  },
  [CHECKIN_EXISTING_STATUS.DELETING]: {
    line1: "Deleting document from OneDrive...",
    line2: "Please, wait...",
  },
};

export function CheckinExistingProcessDialog({ isOpen, onClose, theme }) {
  const {
    loading,
    status,
    step,
    message,
    clear,
  } = useCheckinExistingFlow();

  const isError = status === CHECKIN_EXISTING_STATUS.ERROR && message;
  const showAsError = isOpen && isError;
  const showAsLoading = isOpen && loading;

  const handleClose = () => {
    clear();
    onClose();
  };

  if (!showAsLoading && !showAsError) return null;

  const title = showAsError
    ? resolveCheckinExistingErrorTitle(message)
    : (STEP_TITLES[step] ?? STEP_TITLES[CHECKIN_EXISTING_STATUS.DOWNLOADING]);
  const stepMsg = STEP_MESSAGES[step] ?? STEP_MESSAGES[CHECKIN_EXISTING_STATUS.DOWNLOADING];
  const errorMessage = message || ERROR_DEFAULT_MESSAGE;

  return (
    <ReusableDialog
      isOpen={true}
      onClose={handleClose}
      showHeader={false}
      topBarVariant={showAsError ? "error" : "default"}
      dialogMinHeight={200}
      dialogMinWidth={380}
      bodyMinHeight={80}
      centerContent={true}
      titleContent={
        <Box sx={{ textAlign: "center", py: 0.5 }}>
          <Typography
            variant="h6"
            sx={{
              fontFamily: theme?.fonts?.regular ?? "Poppins, sans-serif",
              fontWeight: "bold",
              color: theme?.colors?.fontColor ?? "inherit",
              fontSize: `${theme.fontSizes?.medium ?? 16}px`,
            }}
          >
            {title}
          </Typography>
        </Box>
      }
      actions={
        <Box sx={{ display: "flex", justifyContent: "center", padding: "8px 24px 12px" }}>
          {showAsError ? (
            <Button
              onClick={handleClose}
              variant="contained"
              sx={{
                backgroundColor: theme?.colors?.themeDarkColor ?? "#4c8d6a",
                fontFamily: theme?.fonts?.regular ?? "Poppins, sans-serif",
                fontSize: `${theme.fontSizes?.normal ?? 12}px`,
                textTransform: "none",
                borderRadius: "20px",
                paddingX: 2,
                color: "white",
              }}
            >
              OK
            </Button>
          ) : (
            <Button
              onClick={handleClose}
              variant="outlined"
              sx={{
                color: theme?.colors?.themeDarkColor ?? "#4c8d6a",
                borderColor: theme?.colors?.themeDarkColor ?? "#4c8d6a",
                fontFamily: theme?.fonts?.regular ?? "Poppins, sans-serif",
                fontSize: `${theme.fontSizes?.normal ?? 12}px`,
                textTransform: "none",
                borderRadius: "20px",
                paddingX: 2,
              }}
            >
              Cancel
            </Button>
          )}
        </Box>
      }
    >
      {showAsError ? (
        <Box sx={{ textAlign: "center", px: 2, py: 0.5 }}>
          <Typography
            variant="body1"
            sx={{
              fontFamily: theme?.fonts?.regular ?? "Poppins, sans-serif",
              color: theme?.colors?.fontColor ?? "inherit",
              fontSize: `${theme.fontSizes?.normal ?? 12}px`,
            }}
          >
            {errorMessage}
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
            py: 1,
          }}
        >
          <Typography
            variant="body1"
            sx={{
              fontFamily: theme?.fonts?.regular ?? "Poppins, sans-serif",
              color: theme?.colors?.fontColor ?? "inherit",
              fontSize: `${theme.fontSizes?.normal ?? 12}px`,
            }}
          >
            {stepMsg.line1}
          </Typography>
          {stepMsg.line2 ? (
            <Typography
              variant="body2"
              sx={{
                fontFamily: theme?.fonts?.regular ?? "Poppins, sans-serif",
                color: theme?.colors?.fontColor ?? "inherit",
                fontSize: `${theme.fontSizes?.normal ?? 12}px`,
              }}
            >
              {stepMsg.line2}
            </Typography>
          ) : null}
          <Loader />
        </Box>
      )}
    </ReusableDialog>
  );
}
